/** @file Shared pieces of vertex insertion: barycentrics, triangle sides, hull slots, the renumbered plan. */
import type { MeshAtt } from '@pose2d/format/types';
import type { TopologyPlan, VertexSource } from './blendSources';

type Tri = [number, number, number];
/** A triangle edge u→w (in its triangle's winding) with the third corner c. */
type Side = { u: number; w: number; c: number };

const px = (p: ArrayLike<number>, v: number): number => p[2 * v] ?? 0;
const py = (p: ArrayLike<number>, v: number): number => p[2 * v + 1] ?? 0;
const cross = (ax: number, ay: number, bx: number, by: number): number => ax * by - ay * bx;

/** Helpers for the insert plans; points `p` are flat [x0, y0, x1, y1, …], −1 in a triangle is the new vertex. */
export const insertParts = {
  /** Corners of the triangle at offset `t`. */
  triAt: (tris: readonly number[], t: number): Tri => [tris[t] ?? 0, tris[t + 1] ?? 0, tris[t + 2] ?? 0],
  /** Affine coordinates of (x, y) over triangle (a, b, c) of `p` (negative outside); null for a degenerate triangle. */
  barycentric(p: ArrayLike<number>, [a, b, c]: Tri, x: number, y: number): Tri | null {
    const ex = px(p, b) - px(p, a), ey = py(p, b) - py(p, a), fx = px(p, c) - px(p, a), fy = py(p, c) - py(p, a);
    const d = cross(ex, ey, fx, fy);
    if (d === 0) return null;
    const wb = cross(x - px(p, a), y - py(p, a), fx, fy) / d;
    const wc = cross(ex, ey, x - px(p, a), y - py(p, a)) / d;
    return [1 - wb - wc, wb, wc];
  },
  /** The edge {a, b} of `tri` in its winding, or null. */
  sideOf(tri: Tri, a: number, b: number): Side | null {
    const k = tri.findIndex((v, i) => { const w = tri[(i + 1) % 3]; return (v === a && w === b) || (v === b && w === a); });
    return k < 0 ? null : { u: tri[k] ?? 0, w: tri[(k + 1) % 3] ?? 0, c: tri[(k + 2) % 3] ?? 0 };
  },
  /** Where a vertex on edge {a, b} goes: between two consecutive hull vertices, else null (an interior edge). */
  hullSlot(a: number, b: number, hull: number): number | null {
    const lo = Math.min(a, b), hi = Math.max(a, b);
    if (hi >= hull) return null;
    if (hi - lo === 1) return hi;
    return lo === 0 && hi === hull - 1 ? hull : null;
  },
  /** The plan with the new vertex (−1 in `tris`) at index k: later vertices shift up by one. */
  planWith(mesh: MeshAtt, k: number, source: VertexSource, tris: readonly number[], onHull: boolean): TopologyPlan {
    const count = mesh.uvs.length / 2;
    const at = (v: number): number => (v < 0 ? k : v < k ? v : v + 1);
    const sources: VertexSource[] = Array.from({ length: count }, (_, v): VertexSource => ({ kind: 'old', v }));
    sources.splice(k, 0, source);
    return { sources, triangles: tris.map(at), hull: mesh.hull + (onHull ? 1 : 0), edges: mesh.edges === undefined ? null : mesh.edges.map(at) };
  },
};
